import { money } from '~/lib/money/money'
import { css } from '~/styled-system/css'
import type { SearchCriteria } from '../validate'
import type { Facet } from './type'

export const planPreferences = {
  freeFlow: {
    label: '飲み放題付',
    facetKey: 'plan.free_flow.1',
    ...trueFalse('freeFlow'),
  },

  buffet:
    import.meta.env.VITE_MODE === 'ppg'
      ? {
          label: '食べ放題',
          facetKey: 'plan.buffet.1',
          ...trueFalse('buffet'),
        }
      : {
          label: '食べ放題（ブッフェ）',
          displayLabel: (
            <>
              食べ放題<span className={css({ fontSize: 'xs' })}>（ブッフェ）</span>
            </>
          ),
          facetKey: 'plan.buffet.1',
          ...trueFalse('buffet'),
        },
} as const satisfies Record<string, Facet>

export const seatPreferences = {
  hotelRestaurant: {
    label: 'ホテル内',
    facetKey: 'restaurant.hotel_restaurant.1',
    ...trueFalse('hotelRestaurant', true),
  },

  nonHotelRestaurant: {
    label: 'ホテル外',
    facetKey: 'restaurant.non_hotel_restaurant.1',
    ...trueFalse('hotelRestaurant', false),
  },

  houseRestaurant: {
    label: '一軒家',
    facetKey: 'restaurant.house_restaurant.1',
    ...trueFalse('houseRestaurant'),
  },

  windowSide: {
    label: '窓際席',
    facetKey: 'seat.window_seat.1',
    ...trueFalse('windowSide'),
  },

  counter: {
    label: 'カウンター席',
    facetKey: 'seat.counter.1',
    ...multiValues('seatTypes', ['COUNTER']),
  },

  terrace: {
    label: 'テラス席',
    facetKey: 'seat.terrace.1',
    ...multiValues('seatTypes', ['TERRACE']),
  },

  bothPrivateRoom: {
    label: '個室（半個室を含む）',
    displayLabel: (
      <>
        個室<span className={css({ fontSize: 'xs' })}>（半個室を含む）</span>
      </>
    ),
    facetKey: 'seat.private_room.1',
    ...multiValues('seatTypes', ['SEMI_PRIVATE_ROOM', 'PRIVATE_ROOM']),
  },

  nonSmoking: {
    label: '禁煙',
    facetKey: 'plan.smoking_permission.0',
    ...trueFalse('smokingAllowed', false),
  },

  smoking: {
    label: '喫煙',
    facetKey: 'plan.smoking_permission.1',
    ...trueFalse('smokingAllowed', true),
  },

  preSchoolers: {
    label: '乳幼児可',
    facetKey: 'restaurant.allow_children.0',
    ...multiValues('childUses', ['PRE_SCHOOLERS']),
  },

  gradeSchoolers: {
    label: '小学生から可',
    facetKey: 'restaurant.allow_children.1',
    ...multiValues('childUses', ['GRADE_SCHOOLERS']),
  },

  cardPaymentAvailable: {
    label: 'カード可',
    facetKey: 'restaurant.can_use_card.1',
    ...trueFalse('cardPaymentAvailable', true),
  },

  parkingAvailable: {
    label: '駐車場',
    facetKey: 'restaurant.has_parking.1',
    ...trueFalse('parkingAvailable', true),
  },
} as const satisfies Record<string, Facet>

export const privateRoom: Facet = {
  label: '個室',
  facetKey: 'seat.private_room.1',
  // 表示だけ「個室」が選択されているときだけ
  // 追加されたり削除したときは両方を操作したい
  ...multiValues('seatTypes', ['SEMI_PRIVATE_ROOM', 'PRIVATE_ROOM']),
  showInList(criteria): boolean {
    return (
      (criteria?.seatTypes?.includes('PRIVATE_ROOM') ?? false) &&
      !criteria?.seatTypes?.includes('SEMI_PRIVATE_ROOM')
    )
  },
  selected(criteria) {
    return criteria?.seatTypes?.includes('PRIVATE_ROOM') ?? false
  },
}

export const withSemiPrivateRoom: Facet = {
  label: '半個室を含む',
  facetKey: 'seat.private_room.1',
  ...multiValues('seatTypes', ['SEMI_PRIVATE_ROOM', 'PRIVATE_ROOM']),
  showInList(_criteria): boolean {
    return false
  },
  remove(current) {
    const { seatTypes, ...rest } = current ?? {}
    const set = new Set(seatTypes)
    set.delete('SEMI_PRIVATE_ROOM')
    return { ...rest, seatTypes: Array.from(set).toSorted() }
  },
}

export function addPricePreferences(
  preferences: readonly Facet[],
  criteria?: SearchCriteria
): readonly Facet[] {
  if (criteria === undefined) {
    return preferences
  }
  return [
    ...preferences,
    priceFacet(criteria, 'lowerBoundPrice'),
    priceFacet(criteria, 'upperBoundPrice'),
  ].filter((p) => p !== undefined)
}

function priceFacet(
  criteria: SearchCriteria,
  key: 'upperBoundPrice' | 'lowerBoundPrice'
): Facet | undefined {
  const price = criteria?.[key]
  if (price === undefined) {
    return undefined
  }
  const m = `${money(price, '円')}`
  const label = key === 'upperBoundPrice' ? `〜${m}` : `${m}〜`
  return {
    label,
    facetKey: key,
    selected: (criteria) => criteria?.[key] !== undefined,
    showInList(criteria) {
      return this.selected(criteria)
    },
    add(current) {
      return current ?? {}
    },
    remove(current) {
      const { [key]: _, ...removed } = current ?? {}
      return removed
    },
  }
}

export const smokingIndifferent = {
  label: '指定なし',
  facetKey: 'plan.smoking_permission.9',
  selected: (criteria) => criteria?.smokingAllowed === undefined,
  showInList(criteria) {
    return this.selected(criteria)
  },
  add(current) {
    return this.remove(current)
  },
  remove(current) {
    const { smokingAllowed: _, ...removed } = current ?? {}
    return removed
  },
} as const satisfies Facet

export const preferences = {
  ...planPreferences,
  ...seatPreferences,
} as const satisfies Record<string, Facet>

function trueFalse(
  key: keyof SearchCriteria,
  value: boolean = true
): Pick<Facet, 'selected' | 'showInList' | 'add' | 'remove'> {
  return {
    selected: (criteria) => criteria?.[key] === value,
    showInList(criteria) {
      return this.selected(criteria)
    },
    add: (current) => ({ ...current, [key]: value }),
    remove(current) {
      const { [key]: _, ...rest } = current ?? {}
      return rest
    },
  }
}

type MultiValues = Pick<SearchCriteria, 'seatTypes' | 'childUses'>

function multiValues<K extends keyof MultiValues>(
  key: K,
  targets: MultiValues[K]
): Pick<Facet, 'selected' | 'showInList' | 'add' | 'remove'> {
  return {
    selected: (criteria) => {
      // set に入れることで型エラー回避できる
      const values = new Set(criteria?.[key])
      return targets?.every((t) => values.has(t)) ?? false
    },
    showInList(criteria) {
      return this.selected(criteria)
    },
    add: (current) => {
      const { [key]: values, ...rest } = current ?? {}
      const set = new Set(values)
      for (const t of targets ?? []) {
        set.add(t)
      }
      return { ...rest, [key]: Array.from(set).toSorted() }
    },
    remove: (current) => {
      const { [key]: values, ...rest } = current ?? {}
      const set = new Set(values)
      for (const t of targets ?? []) {
        set.delete(t)
      }
      if (set.size === 0) {
        return rest
      }
      return { ...rest, [key]: Array.from(set).toSorted() }
    },
  }
}
